import React, { useEffect, useState } from "react";
import StatusBadge from "../../../../common/StatusBadge";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useAssetStatuses from "../../../../store/ReactQueries/assets/assetStatuses/useAssetStatuses";
import useAddAssetStatuses from "../../../../store/ReactQueries/assets/assetStatuses/useAddAssetStatuses";
import useUpdateAssetStatus from "../../../../store/ReactQueries/assets/assetStatuses/useUpdateAssetStatuses";
import useDeleteAssetStatus from "../../../../store/ReactQueries/assets/assetStatuses/useDeleteAssetStatuses";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { AssetStatusInterface } from "../../../../models/AssetModel/AssetStatus";
import { assetStatusesSchema } from "../../../../domain/ValidationSchemas/Asset/assetStatus";

export const AssetStatuses = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<AssetStatusInterface>({
        id: "",
        title: "",
        badgeColor: '#8D97A0',
    });

    const [modal, setModal] = useState<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>({
        add: false,
        edit: false,
        view: false,
    });

    let { data: assetStatus, error } = useAssetStatuses(page, pageSize);

    const addAssetStatus = useAddAssetStatuses(page, pageSize);
    const updateAssetStatus = useUpdateAssetStatus(page, pageSize);
    const deleteAssetStatus = useDeleteAssetStatus(page, pageSize);

    useEffect(() => {
        if (error) ToastMessage.Failed(`Error: ${error.message}`);
    }, [error]);

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            type: "string",
            editable: false,
            width: 200,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            editable: true,
            width: 250,
            cellClassName: 'd-flex align-items-center',
            renderCell: ({ row }) => <StatusBadge name={row.title ? row.title : "No Status"} color={row.badgeColor || '#8D97A0'} width={"fit-content"} fontSize={12} />
        },
        {
            field: "badgeColor",
            headerName: "Badge Color",
            type: "string",
            editable: true,
        },
        {
            field: "createdDate",
            headerName: "CreatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
        {
            field: "updatedDate",
            headerName: "UpdatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
    ];

    const addItems = () => {
        setForm({
            ...form,
            title: "",
            id: "",
            badgeColor: '#8D97A0',
        });
        setModal({ ...modal, add: true });
        setErrors({});
    };

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetStatusesSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: AssetStatusInterface = {
            id: form.id,
            title: form.title,
            badgeColor: form.badgeColor,
        };

        if (upForm.id === "") {
            const { id, ...formData } = upForm;
            addAssetStatus.mutate(formData, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        } else {
            updateAssetStatus.mutate(upForm, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
            deleteAssetStatus.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language["AssetStatuses"]}
            rows={assetStatus || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            showColumnModal={["title", "badgeColor"]}
            hideColumns={["authorId", "id", "tenantId"]}
        />
    );
};
export default AssetStatuses;
